import { useState, useEffect } from 'react';
import { UserPlus, Home, RefreshCw, RotateCcw } from 'lucide-react';
import type { GameId } from '../games/registry';
import { getAvatarById } from './AvatarPicker';

interface GameEndModalProps {
  show: boolean;
  gameType: GameId;
  winner: string | null;
  opponentDisconnected?: boolean;
  opponentUsername?: string;
  opponentAvatar?: string;
  canSendFriendRequest?: boolean;
  rematchStatus?: 'none' | 'requested' | 'accepted';
  onRematch?: () => void;
  onMainMenu: () => void;
  onRequeue: () => void;
  onFriendRequest: () => void;
}

export default function GameEndModal({
  show,
  gameType: _gameType,
  winner,
  opponentDisconnected = false,
  opponentUsername,
  opponentAvatar,
  canSendFriendRequest = false,
  rematchStatus = 'none',
  onRematch,
  onMainMenu,
  onRequeue,
  onFriendRequest
}: GameEndModalProps) {
  const [friendRequestSent, setFriendRequestSent] = useState(false);

  useEffect(() => {
    if (show) setFriendRequestSent(false);
  }, [show]);

  if (!show) return null;

  const avatar = opponentAvatar ? getAvatarById(opponentAvatar) : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-xl p-6 max-w-md w-full">
        {opponentDisconnected ? (
          <>
            <h2 className="text-2xl font-bold mb-4 text-red-400">
              {opponentUsername ? `${opponentUsername} has left!` : 'Your opponent has left!'}
            </h2>
            <p className="text-gray-300 mb-6">
              Would you like to return to the main menu or find a new opponent?
            </p>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-bold mb-4">
              {winner ? `${winner}!` : "It's a Draw!"}
            </h2>
            {opponentUsername && (
              <div className="flex items-center gap-2 text-gray-300 mb-4">
                {avatar && <span className="text-xl">{avatar.emoji}</span>}
                <span>vs {opponentUsername}</span>
              </div>
            )}
            {canSendFriendRequest && !friendRequestSent && (
              <div className="mb-6">
                <button
                  onClick={() => {
                    setFriendRequestSent(true);
                    onFriendRequest();
                  }}
                  className="w-full px-4 py-3 bg-gradient-to-r from-pink-600 to-violet-600 rounded-lg hover:opacity-90 transition flex items-center justify-center gap-2"
                >
                  <UserPlus size={20} />
                  Add {opponentUsername || 'Opponent'} as Friend
                </button>
              </div>
            )}
            {friendRequestSent && (
              <p className="text-green-400 mb-6">
                Friend request sent!
              </p>
            )}
          </>
        )}

        {!opponentDisconnected && onRematch && rematchStatus !== 'accepted' && (
          <button
            onClick={onRematch}
            disabled={rematchStatus === 'requested'}
            className="w-full mb-4 px-4 py-3 bg-gradient-to-r from-pink-600 to-violet-600 rounded-lg hover:opacity-90 transition flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <RotateCcw size={20} />
            {rematchStatus === 'requested' ? 'Waiting for opponent...' : 'Rematch'}
          </button>
        )}
        {rematchStatus === 'accepted' && (
          <p className="text-green-400 text-center mb-4 font-medium">Rematch accepted! Starting new game...</p>
        )}

        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={onMainMenu}
            className="px-4 py-3 bg-white bg-opacity-10 rounded-lg hover:bg-opacity-20 transition flex items-center justify-center gap-2"
          >
            <Home size={20} />
            Main Menu
          </button>
          <button
            onClick={onRequeue}
            className="px-4 py-3 bg-white bg-opacity-10 rounded-lg hover:bg-opacity-20 transition flex items-center justify-center gap-2"
          >
            <RefreshCw size={20} />
            Find New Opponent
          </button>
        </div>
      </div>
    </div>
  );
}
